import clsx from 'clsx';
import React from 'react';
import FileInput from '../FileInput';
import AddIcon from '../../assets/icons/add-icon.svg?react';
import TagsInput from '../TagsInput';
import Button from '../Button';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import Select from '../Select';
import {
  DocumentLibraryControllerAddDocumentDocumentTypeEnum,
  LoginResponseData,
} from '@EHSNavigator/backend';
import WarningIcon from '../../assets/icons/warning.svg?react';
import { useLocalStorage } from '@uidotdev/usehooks';

interface AddDocumentModalProps {
  otherClasses?: string;
  formHandler: any;
  handleClose: () => void;
}

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({
  otherClasses,
  formHandler,
  handleClose,
}) => {
  const addDocumentModalClasses = clsx(otherClasses);
  const [userInfo] = useLocalStorage<LoginResponseData>('userInfo');

  return (
    <div className={addDocumentModalClasses} data-testid="add-document-modal">
      <div className="flex flex-col items-center gap-2.5 text-center">
        <AddIcon className="[&>path]:fill-brand-byzantine h-85 w-85" />
        <h4 className="text-brand-byzantine capitalize">Add New Document</h4>
        <p className="font-inter-variable text-brand-byzantine -tracking-text-area text-base leading-5 font-normal lg:text-lg">
          Add your document information below. Ensure all required fields
          (marked with *) are filled before saving.
        </p>
      </div>
      <form
        action="#"
        onSubmit={formHandler?.handleSubmit}
        className="mt-12 flex flex-col gap-6"
      >
        <TagsInput
          disabled={formHandler?.isSubmitting}
          name="tags"
          value={formHandler?.values?.tags}
          onChange={(e) => formHandler?.setFieldValue('tags', e, true)}
          errorMessage={
            formHandler?.touched.tags && formHandler?.errors?.tags
              ? formHandler?.errors?.tags
              : ''
          }
          otherClasses={{
            container: 'w-full',
            label: 'flex items-center gap-1 relative w-full md:w-fit',
          }}
          label={
            <>
              Tags*
              <button type="button" className="group inline-block md:relative">
                <WarningIcon className="h-5 w-5" />
                <p className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-full left-2/4 z-10 hidden w-full -translate-x-2/4 rounded px-2 py-1 text-center text-xs font-normal shadow-lg group-hover:block group-focus:block md:min-w-44">
                  Type and click enter to add your first option.
                </p>
              </button>
            </>
          }
          handleBlur={formHandler?.handleBlur}
          placeholder=" Add a tag..."
        />
        {userInfo?.user?.role?.name !== 'Employee' && (
          <Select
            label="Document Type*"
            list={Object.entries(
              DocumentLibraryControllerAddDocumentDocumentTypeEnum,
            ).map(([, value]) => ({
              label: value,
              value: value,
            }))}
            value={formHandler.values.documentType}
            disabled={formHandler.isSubmitting}
            placeholder="Select Document Type"
            otherClasses={{
              container: 'w-full',
            }}
            onSelect={(e) => {
              formHandler?.setFieldValue('documentType', e, true);
            }}
            errorMessage={
              formHandler?.touched.documentType &&
              formHandler?.errors.documentType
                ? formHandler?.errors.documentType
                : ''
            }
          />
        )}
        <FileInput
          label="Documents*"
          name="documents"
          selectedFiles={formHandler?.values?.documents}
          onChange={(e) => formHandler?.setFieldValue('documents', e, true)}
          updateAccept
          accept={{
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
              ['.docx'],
          }}
          onBlur={formHandler?.handleBlur}
          customPlaceholder={{
            label1: 'Max 3 documents allowed',
            label2: '.pdf, .doc, .docx',
            label3: 'Upload Document',
          }}
          errorMessage={
            formHandler?.touched.documents && formHandler?.errors?.documents
              ? formHandler?.errors?.documents
              : ''
          }
        />
        <div className="mt-4 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6">
          <Button
            variant="secondary"
            type="button"
            size={useButtonSizeSmall()}
            onClick={() => handleClose()}
            otherClasses="border-transparent text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={formHandler?.isSubmitting}
            disable={formHandler?.isSubmitting}
            size={useButtonSizeSmall()}
            otherClasses=""
          >
            Add Document
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddDocumentModal;
